// src/services/subscriptionApi.ts
import { PlanType, SubscriptionStatus } from '../components/subscription/SubscriptionBadge';

// Define types for the API responses
export interface Subscription {
  id: string;
  userId: number;
  planType: PlanType;
  status: SubscriptionStatus;
  paymentFrequency: 'monthly' | 'yearly' | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  createdAt: string;
  updatedAt: string;
  canceledAt: string | null;
  // Enhanced properties returned by the API
  isActive: boolean;
  isCanceled: boolean;
  isPastDue: boolean;
  isTrialing: boolean;
  cancelAtPeriodEnd: boolean;
  cancelScheduledAt: string | null;
  trialEndsAt: string | null;
  trialDaysRemaining: number;
  currentPeriodEndFormatted: string;
  daysUntilRenewal: number;
  isExpiringSoon: boolean;
  metadata?: Record<string, any>;
}

export interface UsageStats {
  aiQueries: {
    count: number;
    limit: number;
    percentage: number;
    remaining: number;
  };
  contacts: {
    count: number;
    limit: number;
    percentage: number;
    remaining: number;
  };
  todos: {
    count: number;
    limit: number;
    percentage: number;
    remaining: number;
  };
  groups: {
    count: number;
    limit: number;
    percentage: number;
    remaining: number;
  };
  subscription: {
    plan: PlanType;
    isActive: boolean;
    nextTier: PlanType | null;
    upgradedLimits: {
      aiQueries: number;
      contacts: number;
      todos: number;
      groups: number;
    } | null;
  };
  lastUpdated: string;
}

export interface UsageHistory {
  months: string[];
  aiQueries: number[];
  contacts: number[];
  todos: number[];
  groups: number[];
}

export interface SubscriptionHistory {
  id: string;
  userId: number;
  planType: PlanType;
  status: SubscriptionStatus;
  startDate: string;
  endDate: string | null;
  paymentFrequency: string | null;
  source: string;
  createdAt: string;
  metadata?: any;
}

// API Service for Subscription Management
export const subscriptionApi = {
  /**
   * Get the current subscription
   */
  getSubscription: async (): Promise<Subscription> => {
    try {
      const response = await fetch('/api/subscription', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch subscription');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching subscription:', error);
      throw error;
    }
  },
  
  /**
   * Get current usage statistics
   */
  getCurrentUsage: async (): Promise<UsageStats> => {
    try {
      const response = await fetch('/api/subscription/usage', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch usage statistics');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching usage statistics:', error);
      throw error;
    }
  },
  
  /**
   * Get usage history
   */
  getUsageHistory: async (months: number = 6): Promise<UsageHistory> => {
    try {
      const response = await fetch(`/api/subscription/usage-history?months=${months}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch usage history');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching usage history:', error);
      throw error;
    }
  },
  
  /**
   * Upgrade subscription plan
   */
  upgradePlan: async (planType: PlanType, paymentFrequency: 'monthly' | 'yearly'): Promise<Subscription> => {
    try {
      const response = await fetch('/api/subscription/upgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planType, paymentFrequency }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to upgrade subscription');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      throw error;
    }
  },
  
  /**
   * Cancel subscription
   */
  cancelSubscription: async (): Promise<Subscription> => {
    try {
      const response = await fetch('/api/subscription/cancel', {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  },
  
  /**
   * Apply promo code
   */
  applyPromoCode: async (code: string): Promise<{ success: boolean; subscription?: Subscription; error?: string }> => {
    try {
      const response = await fetch('/api/subscription/promo-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to apply promo code');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error applying promo code:', error);
      throw error;
    }
  },
  
  /**
   * Get subscription history
   */
  getSubscriptionHistory: async (): Promise<SubscriptionHistory[]> => {
    try {
      const response = await fetch('/api/subscription/history', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch subscription history');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching subscription history:', error);
      throw error;
    }
  },
  
  /**
   * Create a Stripe checkout session
   */
  createCheckoutSession: async (planType: PlanType, paymentFrequency: 'monthly' | 'yearly'): Promise<{ url: string }> => {
    try {
      const response = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planType, paymentFrequency }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },
  
  /**
   * Create a customer portal session
   */
  createCustomerPortalSession: async (): Promise<{ url: string }> => {
    try {
      const response = await fetch('/api/stripe/portal', {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to create customer portal session');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating customer portal session:', error);
      throw error;
    }
  },
  
  /**
   * Check promo code validity
   */
  checkPromoCode: async (code: string): Promise<{ valid: boolean; details?: any; error?: string }> => {
    try {
      const response = await fetch(`/api/promo-codes/check/${code}`, {
        credentials: 'include'
      });
      
      return await response.json();
    } catch (error) {
      console.error('Error checking promo code:', error);
      return { valid: false, error: 'Failed to check promo code' };
    }
  }
};